import React, { useEffect, useMemo, useState } from 'react';
import ResourceSelector from './resourceSelector/resourceSelectorComponent';
import RecipientSelector from './recipientSelector/recipientSelectorComponent';
import PermissionSelector from './permissionSelector/permissionSelectorComponent';
import { TablePermissions } from './permissionSelector/tablePermissionsFormFields';
import { DataLakePrincipal, Resource } from 'aws-sdk/clients/lakeformation';
import { Database } from 'aws-sdk/clients/glue';
import UseCase from './useCase/useCaseComponent';
import { Button, Flashbar, FlashbarProps, Form, Header, SpaceBetween } from '@amzn/awsui-components-react-v3';
import { Redirect } from 'react-router-dom';
import { scrollUp } from '../../utils/navigation';
import * as validate from '../../../commons/validationUtils';
import { createRequestForDataPermission, getRequest, listDataPermissions } from 'src/api/permissions';
import { capitalize } from './tablePermissionsPage.utils';

import {
  AccessManagementTemplateInfo,
  AccessRequestWorkflowInfo,
  AccessRequestWorkflowInfoList,
  CreateRequestForDataPermissionRequest,
  CreateRequestForDataPermissionResult,
  DataConsumerType,
} from 'aws-sdk/clients/awsdatalakegladstonelambda';

import { Page } from 'src/routes';
import { TemplateRequestDetailsContainer } from 'src/components/permissions/myPermissions/templateRequestDetailsContainer';
import {
  ALL_TABLES_RES_NAME,
  DATA_PERMISSION_LAKE_FORMATION_TYPE,
  DATA_PERMISSION_PUBLISHER_OPTION,
  DATA_PERMISSION_STATUS_ACTIVE,
  FORM_CONTENT_TYPE,
  TEMPLATE_TYPE_ACCESS_MANAGEMENT,
  TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW,
} from 'src/commons/constants';
import { convertApprovalWorkflowIDToLink } from 'src/components/permissions/myPermissions/common';
import { generateApprovalWorkflowMessage, getErrorMessageContent } from 'src/components/common/checkout';
import { isValidPrincipalNotRoot } from '../../../commons/validationUtils';
import {
  applyExistingRequestContextsToAdditionalMetadata,
  findExistingRequestContexts,
  pairsToMapWithArrayValues,
  TemplateMetadata,
  updateTemplateInfo,
} from 'src/components/common/checkout/common';
import { DataAgreementsRequestContextEditor } from 'src/components/common/checkout/dataAgreementsRequestContextEditor';
import _ from 'lodash';
import { DataConsumerTypeSelector } from 'src/components/common/checkout/dataConsumerTypeSelector';
import { OmniDUAContext } from 'src/components/datausageagreement/widgets/OmniDUAContext';

export interface CreateLakeFormationPermissionComponentProps {
  setContentType: any;
  location: any;
  activeGroup: string;
  permissionType: 'request' | 'revoke';
  username: string;
}

const defaultPermissions = {
  tablePermissions: ['SELECT'],
  grantablePermissions: ['SELECT'],
};

export const extractApprovalTemplateIdFromLink = (link: string) => {
  if (link != null) {
    let position = link.lastIndexOf('/');
    return link.substring(position + 1, link.length);
  } else {
    console.log('The approval template link is wrong or not converted properly. ');
  }
};

export const CreateLakeFormationPermissionComponent = (props: CreateLakeFormationPermissionComponentProps) => {
  const [notifications, setNotifications] = useState<FlashbarProps.MessageDefinition[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [redirect, setRedirect] = useState<string>(undefined);
  const [principal, setPrincipal] = useState<DataLakePrincipal>(undefined);
  const [ramRole, setRamRole] = useState<string>(undefined);
  const [catalogId, setCatalogId] = useState<string>(undefined);
  const [database, setDatabase] = useState<Database>(undefined);
  const [databaseName, setDatabaseName] = useState<string>(undefined);
  // For table, the type should be HybridCatalogDataSet, but when the Publisher data permissions are mapped to table objects in use-tables.tsx,
  // the mapping is imperfect (the field is called Owners but it is a string, not a list, for example). Hence, the type is "any" for now.
  // This should be fixed eventually, but it works fine as-is.
  const [table, setTable] = useState<any>(undefined);
  const [tableName, setTableName] = useState<string>(undefined);
  const [permissions, setPermissions] = useState<TablePermissions>(undefined);
  const [resource, setResource] = useState<Resource>(undefined);
  const [publisherRole, setPublisherRole] = useState<string>(undefined);
  const groupRequestedBy: string = props.activeGroup;

  const [useCase, setUseCase] = useState<string>(undefined);
  const [dataConsumerType, setDataConsumerType] = useState<DataConsumerType>('DataConsumer');

  const [additionalMetadata, setAdditionalMetadata] = useState<TemplateMetadata[]>([]);

  const [dataPermissionId, setDataPermissionId] = useState<string>(undefined);
  const [formSubmitted, setFormSubmitted] = useState<boolean>(false);

  useEffect(() => {
    if (table !== undefined) {
      getAllTemplateInfo();
      getDataPermissionIdFromTable(table);
    } else {
      setAdditionalMetadata([]);
    }
  }, [table]);

  useEffect(() => {
    if (database) {
      setCatalogId(database['CatalogId']);
      setTable(undefined);
      setTableName(undefined);
    }
  }, [database]);

  useEffect(() => {
    getAllTemplateInfo();
  }, [principal]);

  useEffect(() => {
    setPermissions(defaultPermissions);
  }, [resource]);

  useEffect(() => {
    // if we were redirected here from the catalog page,
    // replace some state values with the props passed in
    if (props.location.state) {
      setTableName(props.location.state.tableName);
      setDatabaseName(props.location.state.databaseName);
      setCatalogId(props.location.state.catalogId);
    }

    props.setContentType(FORM_CONTENT_TYPE);
  }, []);

  const getAllTemplateInfo = async () => {
    let id = table?.Id;
    if (id === undefined) {
      return;
    }
    if (id == 'All|Tables') {
      if (table != undefined) {
        id = `DS-glueLF|A-${table.IdInfo.CatalogId}|DN-${table.IdInfo.DatabaseName}` + `|R-${table.IdInfo.Region}`;
      } else {
        id =
          `DS-glueLF|A-${props.location.state.catalogId}|DN-${props.location.state.databaseName}` +
          `|R-${props.location.state.region}`;
      }
    }

    let templateMetadataList: TemplateMetadata[] = [];
    const templates = await updateTemplateInfo(
      id,
      undefined,
      props.username,
      () => {},
      principal?.DataLakePrincipalIdentifier ? [principal.DataLakePrincipalIdentifier] : undefined,
    );
    templateMetadataList = templateMetadataList.concat(templates);

    //Group by template ID
    const groupByTemplId = _.groupBy(templateMetadataList, 'templateId');
    //Removed the duplicates based on templateId
    const uniqueTemplates = _.uniqBy(templateMetadataList, 'templateId');
    //Keep track of cart item resource IDs that share the same template and map to its template resource ID
    uniqueTemplates.forEach((uniqTempl) => {
      const hcIdTempResIdPairs: [string, any][] = groupByTemplId[uniqTempl.templateId].map((templ) => [
        templ.id,
        templ.resourceId,
      ]);
      uniqTempl['cartItemResIdToTemplResIdMap'] = pairsToMapWithArrayValues(hcIdTempResIdPairs);
    });
    console.log('Updating state additional metadata with:', uniqueTemplates);

    // get a map from dataAgreementId to requestContextId for any existing request contexts the user has that are eligible for auto-merge.
    const agreementIdToRequestContextIdMap = await findExistingRequestContexts(props);
    // update additionalMetadata to use these request contexts: if a given ARW matches an agreementId then set the requestContextId
    setAdditionalMetadata(
      applyExistingRequestContextsToAdditionalMetadata(uniqueTemplates, agreementIdToRequestContextIdMap),
    );
  };

  const generateAccessManagementTemplateMetadata = () => {
    return additionalMetadata
      .filter((template) => TEMPLATE_TYPE_ACCESS_MANAGEMENT == template.templateType && !!template.accessManagementInfo)
      .map((template) => {
        let accessManagementRequest: AccessManagementTemplateInfo = {
          templateId: template.templateId,
          resourceId: template.resourceId,
        };
        if (template.accessManagementInfo?.simTemplates) {
          let simTickets = template.accessManagementInfo?.simTemplates?.map((simTemplate) => {
            return {
              simTemplateLink: simTemplate.simTemplateLink,
              simTemplateName: simTemplate.simTemplateName,
              simTicketLink: simTemplate.simTicketLink,
            };
          });
          accessManagementRequest.simTicketLinks = simTickets;
        }
        if (template.accessManagementInfo?.approvalsWorkflowTemplate) {
          accessManagementRequest.approvalTemplateId =
            template.accessManagementInfo.approvalsWorkflowTemplate.approvalTemplateId;
          accessManagementRequest.userAlias = template.accessManagementInfo.approvalsWorkflowTemplate.userAlias;
          accessManagementRequest.neededOnDate = convertNeededOnDate(
            template.accessManagementInfo.approvalsWorkflowTemplate.neededOnDate,
          );
          accessManagementRequest.priority = template.accessManagementInfo.approvalsWorkflowTemplate.priority;
        }
        return accessManagementRequest;
      });
  };

  const generateAccessRequestWorkflowMetadata = () => {
    return additionalMetadata
      .filter(
        (template) =>
          TEMPLATE_TYPE_ACCESS_REQUEST_WORKFLOW == template.templateType && template.accessRequestWorkflows?.length > 0,
      )
      .map((template) => {
        let arwInfo: AccessRequestWorkflowInfo = {
          templateId: template.templateId,
          resourceId: template.resourceId,
          templateType: template.templateType,
        };

        const arw = template.accessRequestWorkflows[0];

        if (!!arw?.approvalsWorkflowTemplate?.approvalTemplateId) {
          arwInfo.approvalTemplateId = arw.approvalsWorkflowTemplate.approvalTemplateId;
          arwInfo.userAlias = arw.approvalsWorkflowTemplate.userAlias;
          arwInfo.neededOnDate = convertNeededOnDate(arw.approvalsWorkflowTemplate.neededOnDate);
          arwInfo.priority = arw.approvalsWorkflowTemplate.priority;
        }
        if (!!arw?.dataAgreement?.agreementId) {
          arwInfo.agreementType = arw.dataAgreement.agreementType;
          arwInfo.agreementId = arw.dataAgreement.agreementId;
          arwInfo.requestContextId = arw.requestContextId;
        }
        return arwInfo;
      });
  };

  const convertNeededOnDate = (oldDate: string) => {
    const arr = oldDate.split('-');
    return arr[1] + '/' + arr[2] + '/' + arr[0];
  };

  const createDataPermissionRequest = async () => {
    const accessManagementTemplateMetadata = generateAccessManagementTemplateMetadata();
    const accessRequestWorkflowMetadata = generateAccessRequestWorkflowMetadata();
    let owner = table?.Owners;
    let catalogId = table.IdInfo.CatalogId;
    let databaseName = table.IdInfo.DatabaseName;
    let region = table.IdInfo.Region;
    let tableName = table.IdInfo.TableName;
    if (tableName == ALL_TABLES_RES_NAME) {
      let createRequestForDataPermissionRequest: CreateRequestForDataPermissionRequest = {
        ownerRequestedBy: props.activeGroup,
        ownerRequestedTo: owner,
        useCase: useCase,
        dataConsumerType: dataConsumerType,
        dataPermissionPublisherRole: publisherRole,
        dataPermissionId: dataPermissionId,
        dataLocationRegion: region,
        dataPermissionConsumerRole: ramRole,
        resource: {
          tableWithColumns: null,
          database: null,
          dataCellsFilter: null,
          lFTagPolicy: null,
          table: {
            databaseName: databaseName,
            name: null,
            catalogId: catalogId,
            tableWildcard: {},
          },
        },
        permissions: permissions.tablePermissions,
        permissionsWithGrantOption: isValidPrincipalNotRoot(principal.DataLakePrincipalIdentifier)
          ? []
          : permissions.grantablePermissions,
        dataLakePrincipal: principal.DataLakePrincipalIdentifier,
        accessManagementTemplateMetadata: accessManagementTemplateMetadata,
        accessRequestWorkflowMetadata: accessRequestWorkflowMetadata,
      };
      let createRequestForDataPermissionResult: CreateRequestForDataPermissionResult =
        await createRequestForDataPermission(createRequestForDataPermissionRequest);
      return createRequestForDataPermissionResult;
    } else {
      let createRequestForDataPermissionRequest: CreateRequestForDataPermissionRequest = {
        ownerRequestedBy: props.activeGroup,
        ownerRequestedTo: owner,
        useCase: useCase,
        dataConsumerType: dataConsumerType,
        dataPermissionPublisherRole: publisherRole,
        dataPermissionId: dataPermissionId,
        dataLocationRegion: region,
        dataPermissionConsumerRole: ramRole,
        resource: {
          tableWithColumns: null,
          database: null,
          dataCellsFilter: null,
          lFTagPolicy: null,
          table: {
            databaseName: databaseName,
            name: tableName,
            catalogId: catalogId,
            tableWildcard: null,
          },
        },
        permissions: permissions.tablePermissions,
        permissionsWithGrantOption: isValidPrincipalNotRoot(principal.DataLakePrincipalIdentifier)
          ? []
          : permissions.grantablePermissions,
        dataLakePrincipal: principal.DataLakePrincipalIdentifier,
        accessManagementTemplateMetadata: accessManagementTemplateMetadata,
        accessRequestWorkflowMetadata: accessRequestWorkflowMetadata,
      };

      let createRequestForDataPermissionResult: CreateRequestForDataPermissionResult =
        await createRequestForDataPermission(createRequestForDataPermissionRequest);
      return createRequestForDataPermissionResult;
    }
  };

  const isColumnPermissionWithNoSelectedColumns = () => {
    if (resource != null && resource.TableWithColumns != null) {
      let columnResource = resource.TableWithColumns;
      if (columnResource.ColumnNames != null && columnResource.ColumnNames.length == 0) {
        return true;
      }
      if (
        columnResource.ColumnWildcard != null &&
        columnResource.ColumnWildcard.ExcludedColumnNames != null &&
        columnResource.ColumnWildcard.ExcludedColumnNames.length == 0
      ) {
        return true;
      }
    }
    return false;
  };

  const handleConfirm = async () => {
    if (!useCase) {
      setFormSubmitted(true);
      return;
    }

    setLoading(true);

    try {
      let response;
      response = await createDataPermissionRequest();
      const requestId = response.requestId;
      let approvalWorkflowLinks = [];
      const getRequestResponse = await getRequest({ requestId: requestId });
      const accessManagementInfoList = getRequestResponse?.requestItem?.additionalMetadata?.accessManagementInfoList;
      if (accessManagementInfoList) {
        accessManagementInfoList.forEach((aMIS) => {
          if (aMIS.approvalWorkflowId) {
            const approvalWorkflowLink = convertApprovalWorkflowIDToLink(aMIS.approvalWorkflowId);
            approvalWorkflowLinks.push(approvalWorkflowLink);
          }
        });
      }

      let newNotifications = [
        ...notifications,
        {
          type: 'success',
          content: `Lake Formation request '${requestId}' created.`,
          dismissible: true,
          buttonText: 'View requests',
          onButtonClick: () => setRedirect(Page.REQUESTS),
          onDismiss: () => setNotifications([]),
        },
      ];

      if (approvalWorkflowLinks.length > 0) {
        const approvalWorkflowMessage = generateApprovalWorkflowMessage(approvalWorkflowLinks);
        newNotifications.push({
          type: 'info',
          content: approvalWorkflowMessage,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        });
      }
      setNotifications(newNotifications);
    } catch (err) {
      const failMessage = 'Failed to submit the access request';
      const errMsgContent = getErrorMessageContent(err);
      setNotifications([
        {
          type: 'error',
          header: failMessage,
          content: errMsgContent,
          dismissible: true,
          onDismiss: () => setNotifications([]),
        },
      ]);
    }
    setLoading(false);
    scrollUp();
  };

  const allFieldsSet = () => {
    return (
      // Validate Objects
      validate.isNotFalsy(table) &&
      validate.isNotFalsy(database) &&
      validate.isNotFalsy(principal) &&
      validate.isNotFalsy(ramRole) &&
      validate.isNotFalsy(permissions) &&
      // Validate Request information
      validate.isValidGroup(groupRequestedBy) &&
      validate.isValidGroup(table.Owners[0]) &&
      validate.isValidRole(table.LakeFormationRoleARN) &&
      validate.isNotFalsy(table.IdInfo.Region) &&
      (validate.isValidAccount(principal.DataLakePrincipalIdentifier) ||
        validate.isValidPrincipalNotRoot(principal.DataLakePrincipalIdentifier)) &&
      validate.isNotFalsy(permissions?.tablePermissions?.length) &&
      validateAdditionalMetadata()
    );
  };

  const validateAdditionalMetadata = () => {
    let validation = [];
    additionalMetadata.map((t) => {
      // Validate AMI templates
      if (!!t.accessManagementInfo) {
        // AMI - SIM tickets
        if (t.accessManagementInfo.simTemplates) {
          t.accessManagementInfo.simTemplates.map((st) => {
            if (!st.simTicketLink) {
              validation.push(true);
            } else {
              validation.push(false);
            }
          });
        }
        // AMI - Amazon approvals
        if (t.accessManagementInfo.approvalsWorkflowTemplate) {
          if (!t.accessManagementInfo.approvalsWorkflowTemplate.neededOnDate) {
            validation.push(true);
          } else if (!t.accessManagementInfo.approvalsWorkflowTemplate.priority) {
            validation.push(true);
          } else {
            validation.push(false);
          }
        }
      }
      // Validate ARW templates
      if (!!t.accessRequestWorkflows) {
        for (const arw of t.accessRequestWorkflows) {
          if (!arw.requestContextId) {
            validation.push(true);
          } else {
            validation.push(false);
          }
          if (arw.dataAgreement) {
            if (!arw.dataAgreement.agreementId || !arw.dataAgreement.agreementType) {
              validation.push(true);
            } else {
              validation.push(false);
            }
          }
          if (arw.approvalsWorkflowTemplate) {
            if (!arw.approvalsWorkflowTemplate.neededOnDate) {
              validation.push(true);
            } else if (!arw.approvalsWorkflowTemplate.priority) {
              validation.push(true);
            } else {
              validation.push(false);
            }
          }
        }
      }
    });
    return validation.every((val) => val == false);
  };

  const getDataPermissionIdFromTable = async (table) => {
    let owner = table?.Owners;
    let databaseName = table?.IdInfo.DatabaseName;
    let catalogId = table?.IdInfo.CatalogId;
    let region = table?.IdInfo.Region;
    let tableName = table?.IdInfo.TableName;

    let listDatasetPermissionRequest = {
      ownerId: owner,
      region: region,
      resource: {
        table: {
          databaseName: databaseName,
          name: null,
          catalogId: catalogId,
          tableWildcard: null,
        },
      },
      option: DATA_PERMISSION_PUBLISHER_OPTION,
      status: DATA_PERMISSION_STATUS_ACTIVE,
      type: DATA_PERMISSION_LAKE_FORMATION_TYPE,
      nextToken: null,
    };

    let permissionId = undefined;
    try {
      let listDatasetPermissionResponse = await listDataPermissions(listDatasetPermissionRequest);
      if (listDatasetPermissionResponse?.dataPermissionList !== undefined) {
        for (let permission of listDatasetPermissionResponse?.dataPermissionList) {
          if (
            (permission.resource.table !== undefined &&
              (permission.resource.table.name == null || permission.resource.table.name == tableName)) ||
            (permission.resource.tableWithColumns !== undefined &&
              (permission.resource.tableWithColumns.name == tableName ||
                (permission.resource.tableWithColumns.name == 'ALL_TABLES' &&
                  permission.resource.tableWithColumns.databaseName == databaseName)))
          ) {
            permissionId = permission.dataPermissionId;
            setPublisherRole(permission.audit.PublisherRole);
          }
        }
      }
    } catch (err) {
      console.log('Exception while getting permission', err);
    }
    setDataPermissionId(permissionId);
  };

  const shouldDisplayAccessManagementTemplate = () => {
    return additionalMetadata.length > 0 && principal && principal.DataLakePrincipalIdentifier;
  };

  const shouldDisplayARWTemplate = () => {
    return (
      additionalMetadata.length > 0 &&
      additionalMetadata.filter((metadata) => metadata.accessRequestWorkflows?.length > 0).length > 0
    );
  };

  const extractAccountIdFromDataLakePrincipal = () => {
    return principal?.DataLakePrincipalIdentifier?.match(/\d{12}/)?.[0];
  };

  const omniContext: OmniDUAContext = useMemo(
    () => ({
      consumerAccountId: extractAccountIdFromDataLakePrincipal(),
    }),
    [principal],
  );

  if (redirect) {
    return <Redirect push to={redirect} />;
  }

  return (
    <SpaceBetween size={'l'} direction={'vertical'}>
      {notifications?.length > 0 && <Flashbar items={notifications} />}
      <Form
        header={
          <Header
            variant='h1'
            description={`Choose the access permissions to ${props.permissionType.toLowerCase()} to this or an external account.`}
          >
            {`${capitalize(props.permissionType)} permissions`}
          </Header>
        }
        actions={
          <div>
            <Button variant='link' onClick={() => setRedirect(Page.MY_DATASETS)}>
              Cancel
            </Button>

            <Button
              variant='primary'
              onClick={handleConfirm}
              loading={loading}
              disabled={!allFieldsSet() || isColumnPermissionWithNoSelectedColumns()}
            >
              {capitalize(props.permissionType)}
            </Button>
          </div>
        }
      >
        <SpaceBetween size='l'>
          <ResourceSelector
            onTableChange={setTable}
            onDatabaseChange={setDatabase}
            databaseName={databaseName}
            tableName={tableName}
            catalogId={catalogId}
            activeGroup={props.activeGroup}
          />
          <RecipientSelector activeGroup={props.activeGroup} onChange={setPrincipal} onRamRoleChange={setRamRole} />
          <PermissionSelector
            table={table}
            database={database}
            principal={principal}
            onChange={setResource}
            permissions={permissions}
            onPermissionsChange={setPermissions}
          />
          <UseCase onChange={setUseCase} value={useCase} formSubmitted={formSubmitted} />
          <DataConsumerTypeSelector dataConsumerType={dataConsumerType} setDataConsumerType={setDataConsumerType} />
          {shouldDisplayARWTemplate() && (
            <DataAgreementsRequestContextEditor
              {...props}
              ref={undefined}
              displaySaveButton={true}
              additionalMetadata={additionalMetadata}
              setAdditionalMetadata={setAdditionalMetadata}
              omniContext={omniContext}
            />
          )}
          {shouldDisplayAccessManagementTemplate() && (
            <TemplateRequestDetailsContainer
              additionalMetadata={additionalMetadata}
              setAdditionalMetadata={setAdditionalMetadata}
            />
          )}
        </SpaceBetween>
      </Form>
    </SpaceBetween>
  );
};
